import React, { useState, useEffect, FC } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Box, 
  Typography,
  Alert,
  CircularProgress,
  Avatar,
  Paper
} from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RecommendIcon from '@mui/icons-material/Recommend';
import ConstructionIcon from '@mui/icons-material/Construction';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ShareIcon from '@mui/icons-material/Share';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SchoolIcon from '@mui/icons-material/School';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import StarIcon from '@mui/icons-material/Star';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DomainIcon from '@mui/icons-material/Domain';

import { Card } from './ui/card';
import { AnalysisNav } from './ui/analysis-nav';
// import { DashboardHeader } from './DashboardHeader'; // Not used directly in this component
import { StarsBackground } from './ui/stars-background';
import ReactMarkdown from 'react-markdown';
import { TeamAnalysisProgressCard } from './ui/team-analysis-progress-card';

import { Button as UiButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RainbowButton } from "@/components/ui/rainbow-button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Plus, Trash } from "lucide-react";



interface LinkedInProfile {
  id: number;
  about: string;
  avatar: string;
  current_company: {
    name: string;
    title: string;
  };
  education: Array<{
    institute_name: string;
    end_year: string;
    degree?: string;
    field?: string;
  }>;
  experience: Array<{
    company: string;
    title: string;
    duration: string;
    description?: string;
  }>;
  analysis?: TeamAnalysis;
}

interface TeamAnalysis {
  individual_scores: {
    [profileId: number]: {
      education: number;
      experience: number;
      domain_expertise: number;
      entrepreneurial_history: number;
    };
  };
  team_scores: {
    skill_complementarity: number;
    role_distribution: number;
    experience_balance: number;
    track_record_fit: number;
  };
  recommendations: string[];
  gpt_analysis?: {
    raw_analysis: string;
  };
  structured_analysis?: {
    executive_summary: string;
    individual_analysis: Array<{
      name: string;
      core_skills: string[];
      experience: string;
      education: string;
      strengths: string[];
      weaknesses: string[];
    }>;
    team_analysis: {
      strengths: string[];
      weaknesses: string[];
      opportunities: string[];
      threats: string[];
    };
    recommendations: string[];
  };
}



const Team: React.FC = () => {
  const { id: analysisId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [linkedinUrls, setLinkedinUrls] = useState<string[]>(['']);
  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState<'error' | 'success'>('error');
  const [profiles, setProfiles] = useState<LinkedInProfile[]>([]);
  const [teamAnalysis, setTeamAnalysis] = useState<TeamAnalysis | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [credits, setCredits] = useState(0);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [analysisStatus, setAnalysisStatus] = useState<'idle' | 'pending' | 'processing' | 'completed' | 'failed'>('idle');
  const [analysisStartTime, setAnalysisStartTime] = useState<string | null>(null);

  useEffect(() => {
    if (!analysisId) {
      const storedAnalysisId = localStorage.getItem('currentAnalysisId');
      if (storedAnalysisId) {
        navigate(`/founders/${storedAnalysisId}`, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [analysisId, navigate]);

  const fetchCredits = async () => {
    try {
      setCreditsLoading(true);
      const response = await api.get('/api/credits/balance');
      setCredits(response.data.credits);
    } catch (err) {
      console.error('Error fetching credits:', err);
    } finally {
      setCreditsLoading(false);
    }
  };

  useEffect(() => {
    const fetchProfiles = async () => {
      if (!analysisId) return;
      
      try {
        setLoading(true);
        setError(null);
        await fetchCredits();
        
        const response = await api.get(`/api/linkedin/profiles/${analysisId}`);
  
        if (response.data.success) {
          setProfiles(response.data.profiles || []);
          if (response.data.team_analysis) {
            setTeamAnalysis(response.data.team_analysis);
            setAnalysisStatus('completed');
          } else if (response.data.profiles && response.data.profiles.length > 0) {
            // Check if analysis is in progress
            const hasAnalysis = response.data.profiles.some((profile: any) => profile.analysis);
            if (!hasAnalysis && response.data.analysis_status) {
              setAnalysisStatus(response.data.analysis_status);
              setAnalysisStartTime(response.data.analysis_started_at || new Date().toISOString());
            } else if (hasAnalysis) {
              setAnalysisStatus('completed');
            }
          }
        } else {
          setError(response.data.error || 'Failed to fetch team profiles');
        }
      } catch (error: any) {
        console.error('Error fetching profiles:', error);
        if (error.response?.status === 401) {
          setError('Session expired. Please log in again.');
          navigate('/login');
        } else if (error.response?.status === 404) {
          setError('Analysis not found. Please try again.');
        } else {
          setError(error.response?.data?.error || 'Failed to fetch team profiles');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, [analysisId, api, navigate]);

  // Poll for analysis updates when status is pending or processing
  useEffect(() => {
    if (analysisStatus === 'pending' || analysisStatus === 'processing') {
      const pollInterval = setInterval(async () => {
        try {
          const response = await api.get(`/api/linkedin/profiles/${analysisId}`);
          
          if (response.data.success) {
            if (response.data.team_analysis) {
              setTeamAnalysis(response.data.team_analysis);
              setAnalysisStatus('completed');
              clearInterval(pollInterval);
            } else if (response.data.analysis_status) {
              setAnalysisStatus(response.data.analysis_status);
              if (response.data.analysis_status === 'failed') {
                clearInterval(pollInterval);
              }
            }
          }
        } catch (error) {
          console.error('Error polling for updates:', error);
        }
      }, 5000); // Poll every 5 seconds
      
      // Clean up interval on unmount or status change
      return () => clearInterval(pollInterval);
    }
  }, [analysisStatus, analysisId, api]);

  const handleLinkedinSubmit = async () => {
    const nonEmptyUrls = linkedinUrls.filter(url => url.trim() !== '');
    if (nonEmptyUrls.length === 0) {
      setAlertMessage('Please enter at least one LinkedIn profile URL.');
      setAlertType('error');
      setShowAlert(true);
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(`/api/linkedin/profiles/${analysisId}`, {
        profile_urls: nonEmptyUrls,
      });

      if (response.data.success) {
        setAlertMessage(response.data.message || 'LinkedIn profiles added successfully!');
        // Determine if this is a success or just a notification (like 'queued')
        setAlertType(response.data.status === 'queued' ? 'success' : 'success');
        setShowAlert(true);
        setLinkedinUrls(['']);
        
        // Set analysis status based on response
        if (response.data.status === 'queued' || response.data.status === 'pending') {
          setAnalysisStatus('pending');
          setAnalysisStartTime(new Date().toISOString());
        } else if (response.data.status === 'processing') {
          setAnalysisStatus('processing');
          setAnalysisStartTime(new Date().toISOString());
        }
        
        // Start polling for updates
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        setAlertMessage(response.data.error || 'Failed to add LinkedIn profiles');
        setAlertType('error');
        setShowAlert(true);
      }
    } catch (error: any) {
      console.error('Error adding LinkedIn profiles:', error);
      setAlertMessage(error.response?.data?.error || 'Failed to add LinkedIn profiles');
      setAlertType('error');
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUrlChange = (index: number, value: string) => {
    const newUrls = [...linkedinUrls];
    newUrls[index] = value;
    setLinkedinUrls(newUrls);
  };

  const addUrlInput = () => {
    setLinkedinUrls([...linkedinUrls, '']);
  };

  const removeUrlInput = (index: number) => {
    if (linkedinUrls.length > 1) {
      const newUrls = linkedinUrls.filter((_, i) => i !== index);
      setLinkedinUrls(newUrls);
    }
  };

  // Helper function to check if a value is empty or meaningless
  const isEmptyValue = (value: any): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (typeof value === 'number' && value === 0) return true;
    if (typeof value === 'string' && value === '0') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'object' && Object.keys(value).length === 0) return true;
    return false;
  };

  // Helper function to format keys for display (handles both snake_case and camelCase)
  const formatKeyForDisplay = (key: string): string => {
    // First handle snake_case by replacing underscores with spaces
    let formatted = key.replace(/_/g, ' ');
    
    // Then handle camelCase by adding spaces before capital letters
    formatted = formatted.replace(/([A-Z])/g, ' $1');
    
    // Trim any extra spaces and ensure first letter of each word is capitalized
    return formatted
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to render a value based on its type
  const renderValue = (value: any) => {
    if (isEmptyValue(value)) return null;
    
    if (Array.isArray(value)) {
      // Filter out empty values in arrays
      const filteredItems = value.filter(item => !isEmptyValue(item));
      if (filteredItems.length === 0) return null;
      
      return (
        <ul className="list-disc pl-5 space-y-2 text-left">
          {filteredItems.map((item, idx) => (
            <li key={idx} className="text-gray-700 text-base leading-relaxed" style={{ fontFamily: '"Open Sans", sans-serif' }}>
              {typeof item === 'string' ? item : JSON.stringify(item)}
            </li>
          ))}
        </ul>
      );
    }
    
    if (typeof value === 'object' && value !== null) {
      // Filter out empty values in objects
      const entries = Object.entries(value);
      const filteredEntries = entries.filter(([_, val]) => !isEmptyValue(val));
      if (filteredEntries.length === 0) return null;
      
      return (
        <div className="space-y-4 text-left">
          {filteredEntries.map(([key, val]) => (
            <div key={key} className="mb-4">
              <h4 className="text-lg font-semibold text-gray-800 mb-1 text-left" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                {formatKeyForDisplay(key)}
              </h4>
              <div className="text-base text-gray-700 py-1" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                {typeof val === 'string' || typeof val === 'number' 
                  ? val 
                  : Array.isArray(val)
                    ? val.filter(v => !isEmptyValue(v)).join(', ')
                    : JSON.stringify(val)}
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    // For strings and numbers
    return <p className="text-gray-700 text-base text-left leading-relaxed" style={{ fontFamily: '"Open Sans", sans-serif' }}>{value}</p>;
  };

  // Helper function to get the appropriate icon for a section title with color
  const getSectionIcon = (title: string) => {
    const lowerTitle = title.toLowerCase();
    
    if (lowerTitle.includes('executive summary')) 
      return <LightbulbOutlinedIcon className="mr-2" sx={{ color: '#FFD700' }} />; // Gold
    
    if (lowerTitle.includes('individual')) 
      return <PersonOutlineIcon className="mr-2" sx={{ color: '#4CAF50' }} />; // Green
    
    if (lowerTitle.includes('team')) 
      return <GroupsIcon className="mr-2" sx={{ color: '#2196F3' }} />; // Blue
    
    if (lowerTitle.includes('swot')) 
      return <AssessmentIcon className="mr-2" sx={{ color: '#9C27B0' }} />; // Purple
    
    if (lowerTitle.includes('recommendation')) 
      return <RecommendIcon className="mr-2" sx={{ color: '#FF9800' }} />; // Orange
    
    if (lowerTitle.includes('execution')) 
      return <ConstructionIcon className="mr-2" sx={{ color: '#F44336' }} />; // Red
    
    if (lowerTitle.includes('market')) 
      return <BusinessCenterIcon className="mr-2" sx={{ color: '#00BCD4' }} />; // Cyan
    
    if (lowerTitle.includes('network')) 
      return <ShareIcon className="mr-2" sx={{ color: '#3F51B5' }} />; // Indigo
    
    if (lowerTitle.includes('risk')) 
      return <WarningAmberIcon className="mr-2" sx={{ color: '#FF5722' }} />; // Deep Orange
    
    if (lowerTitle.includes('dynamics')) 
      return <PsychologyIcon className="mr-2" sx={{ color: '#673AB7' }} />; // Deep Purple
    
    if (lowerTitle.includes('education')) 
      return <SchoolIcon className="mr-2" sx={{ color: '#009688' }} />; // Teal
    
    if (lowerTitle.includes('experience')) 
      return <WorkOutlineIcon className="mr-2" sx={{ color: '#795548' }} />; // Brown
    
    if (lowerTitle.includes('skill')) 
      return <StarOutlineIcon className="mr-2" sx={{ color: '#FFEB3B' }} />; // Yellow
    
    // Default icon
    return <LightbulbOutlinedIcon className="mr-2" sx={{ color: '#607D8B' }} />; // Blue Grey
  };

  // Helper function to render a card for any key-value pair
  const renderGenericCard = (title: string, content: any) => {
    // Skip rendering if content is empty or meaningless
    if (!content) return null;
    
    // Enhanced check for empty content
    if (typeof content === 'string' && content.trim() === '') return null;
    if (Array.isArray(content) && (content.length === 0 || content.every(item => !item || (typeof item === 'string' && item.trim() === '')))) return null;
    if (typeof content === 'object' && Object.keys(content).length === 0) return null;
    
    // For objects, check if all values are empty
    if (typeof content === 'object' && !Array.isArray(content)) {
      const hasNonEmptyValue = Object.values(content).some(value => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'string' && value.trim() === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      });
      
      if (!hasNonEmptyValue) return null;
    }
    
    return (
    <Card className="p-6 relative bg-transparent border border-gray-200 shadow-2xl backdrop-blur-sm rounded-xl mb-6">
      <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
        <StarsBackground />
      </div>
      <div className="relative z-10">
        <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center text-left border-b border-gray-300 pb-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>
          {getSectionIcon(title)}
          {title}
        </h2>
        
        <div className="space-y-4 text-left">
          {typeof content === 'string' ? (
            <p className="text-base text-gray-700" style={{ fontFamily: '"Open Sans", sans-serif' }}>{content}</p>
          ) : Array.isArray(content) ? (
            <ul className="list-disc pl-5 space-y-1">
              {content.map((item, idx) => (
                <li key={idx} className="text-base text-gray-700" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  {typeof item === 'string' ? item : JSON.stringify(item)}
                </li>
              ))}
            </ul>
          ) : (
            <div className="space-y-4">
              {Object.entries(content).map(([key, value], idx) => {
                const formattedKey = key.replace(/_/g, ' ')
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
                
                return (
                  <div key={idx} className="mb-3">
                    <h4 className="text-base font-medium text-gray-800 mb-1">{formattedKey}</h4>
                    
                    {Array.isArray(value) ? (
                      <ul className="list-disc pl-5 space-y-1">
                        {value.map((item, itemIdx) => (
                          <li key={itemIdx} className="text-gray-700 text-base" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                            {typeof item === 'string' ? item : JSON.stringify(item)}
                          </li>
                        ))}
                      </ul>
                    ) : typeof value === 'string' ? (
                      <p className="text-gray-700">{value}</p>
                    ) : (
                      <pre className="text-base bg-gray-50 p-4 rounded-lg overflow-auto" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

// Helper function to render structured cards
const renderStructuredCard = (key: string, formattedKey: string, filteredEntries: [string, any][]) => {
  // Skip rendering if there are no entries with actual values
  if (filteredEntries.length === 0) return null;
    
  // Additional check to ensure there's at least one non-empty value
  const hasActualContent = filteredEntries.some(([_, value]) => {
    // Check if the value has any meaningful content
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0 && value.some(item => item !== null && item !== undefined && item !== '');
    if (typeof value === 'object' && value !== null) {
      return Object.values(value).some(v => v !== null && v !== undefined && v !== '');
    }
    return value !== null && value !== undefined;
  });
    
  if (!hasActualContent) return null;
  
  // Helper function to get the appropriate icon for a section
  const getSectionIcon = (section: string) => {
    const lowerSection = section.toLowerCase();
    if (lowerSection.includes('execution')) return <ConstructionIcon className="mr-2" sx={{ color: '#FF9800' }} />;
    if (lowerSection.includes('market')) return <BusinessCenterIcon className="mr-2" sx={{ color: '#2196F3' }} />;
    if (lowerSection.includes('network')) return <ShareIcon className="mr-2" sx={{ color: '#9C27B0' }} />;
    return <AssessmentIcon className="mr-2" sx={{ color: '#4CAF50' }} />;
  };
  
  return (
    <Card className="p-6 relative bg-transparent border border-gray-200 shadow-2xl backdrop-blur-sm rounded-xl mb-6" key={key}>
      <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
        <StarsBackground />
      </div>
      <div className="relative z-10">
        <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center text-left border-b border-gray-300 pb-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>
          {getSectionIcon(formattedKey)}
          {formattedKey}
        </h2>
        
        <div className="space-y-4">
          {filteredEntries.map(([entryKey, entryValue], index) => {
            const formattedEntryKey = formatKeyForDisplay(entryKey);
            return (
              <div key={index} className="mb-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-3 text-left" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  {formattedEntryKey}
                </h3>
                <div className="text-left ml-1">
                  {renderValue(entryValue)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

// The helper functions isEmptyValue and formatKeyForDisplay are already defined above

// Using the formatKeyForDisplay function defined above

// Render individual analysis cards with improved styling
const renderIndividualAnalysis = (individuals: any[]) => {
  if (!individuals || individuals.length === 0) {
    return null;
  }
  
  
  return (
    <Card className="p-6 relative bg-transparent border border-gray-200 shadow-2xl backdrop-blur-sm rounded-xl mb-6">
      <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
        <StarsBackground />
      </div>
      <div className="relative z-10">
        <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center text-left border-b border-gray-300 pb-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>
          <PersonIcon className="mr-2" sx={{ color: '#4CAF50' }} />
          Individual Analysis
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {individuals.map((person, index) => {
            // Extract core skills from the object structure if it exists
            let coreSkillsArray: string[] = [];
            if (person.core_skills) {
              // Handle different possible structures of core_skills
              if (Array.isArray(person.core_skills)) {
                coreSkillsArray = person.core_skills;
              } else if (typeof person.core_skills === 'object') {
                // Extract skills from object structure
                coreSkillsArray = Object.entries(person.core_skills)
                  .filter(([_, value]) => value) // Filter out falsy values
                  .map(([key]) => formatKeyForDisplay(key)); // Format keys for display
              }
            }
            
            // Extract achievement history if it exists
            const achievementHistory = person.achievement_history || {};
            const notableProjects = achievementHistory.notable_projects || [];
            const impactMetrics = achievementHistory.impact_metrics || [];
            
            // Check if sections have content
            const hasSkills = coreSkillsArray.length > 0;
            const hasProjects = notableProjects.length > 0;
            const hasImpact = impactMetrics.length > 0;
            
            return (
              <div key={index} className="p-6 relative bg-transparent border border-gray-200 shadow-lg backdrop-blur-sm rounded-xl">
                <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl opacity-5">
                  <StarsBackground />
                </div>
                
                <div className="relative z-10">
                  {/* Header with name */}
                  <div className="flex items-center mb-6 border-b border-gray-200 pb-4">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-3 text-blue-600 text-xl font-bold border-2 border-gray-200">
                      {person.name && person.name.split(' ').map((n: string) => n[0]).join('')}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Open Sans", sans-serif' }}>{person.name}</h3>
                    </div>
                  </div>
                  
                  {/* Notable Projects Section */}
                  {hasProjects && (
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold mb-3 text-gray-800 flex items-center" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                        <WorkOutlineIcon className="mr-2" sx={{ color: '#FF9800', fontSize: 20 }} />
                        Notable Projects
                      </h4>
                      <ul className="list-disc pl-8 space-y-1">
                        {notableProjects.map((project: string, idx: number) => (
                          <li key={idx} className="text-gray-700" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                            {project}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Impact Metrics Section */}
                  {hasImpact && (
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold mb-3 text-gray-800 flex items-center" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                        <StarIcon className="mr-2" sx={{ color: '#4FC3F7', fontSize: 20 }} />
                        Impact Metrics
                      </h4>
                      <ul className="list-disc pl-8 space-y-1">
                        {impactMetrics.map((metric: string, idx: number) => (
                          <li key={idx} className="text-gray-700" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                            {metric}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Core Skills Section */}
                  {hasSkills && (
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold mb-3 text-gray-800 flex items-center" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                        <StarOutlineIcon className="mr-2" sx={{ color: '#4CAF50', fontSize: 20 }} />
                        Core Skills
                      </h4>
                      <div className="flex flex-wrap gap-2 pl-3">
                        {coreSkillsArray.map((skill: string, idx: number) => (
                          <span key={idx} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* If no sections are available, show a message */}
                  {!hasSkills && !hasProjects && !hasImpact && (
                    <div className="mb-6 text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-gray-600" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                        No detailed information available for this team member.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

  // Render SWOT analysis
  const renderSwotAnalysis = (teamAnalysis: any) => {
    if (!teamAnalysis) return null;
    
    // Extract SWOT categories
    const swotCategories = [
      { key: 'strengths', color: 'bg-green-50 border-green-200', title: 'Strengths', icon: <StarOutlineIcon sx={{ color: '#4CAF50' }} /> },
      { key: 'weaknesses', color: 'bg-red-50 border-red-200', title: 'Weaknesses', icon: <WarningAmberIcon sx={{ color: '#F44336' }} /> },
      { key: 'opportunities', color: 'bg-blue-50 border-blue-200', title: 'Opportunities', icon: <LightbulbOutlinedIcon sx={{ color: '#2196F3' }} /> },
      { key: 'threats', color: 'bg-amber-50 border-amber-200', title: 'Threats', icon: <AlertTriangle className="text-amber-500" size={18} /> }
    ];
    
    // Check if any SWOT category exists
    const hasSwot = swotCategories.some(category => {
      const analysis = teamAnalysis as Record<string, any>;
      return analysis[category.key] && analysis[category.key].length > 0;
    });
    
    if (!hasSwot) return null;
    
    return (
      <Card className="p-6 relative bg-transparent border border-gray-200 shadow-2xl backdrop-blur-sm rounded-xl mb-6">
        <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
          <StarsBackground />
        </div>
        <div className="relative z-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center text-left border-b border-gray-300 pb-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>
            <GroupsIcon className="mr-2" sx={{ color: '#3b82f6' }} />
            Team SWOT Analysis
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {swotCategories.map(category => {
              // Skip empty categories
              if (!teamAnalysis[category.key] || teamAnalysis[category.key].length === 0) return null;
              
              return (
                <div key={category.key} className={`p-4 rounded-lg border ${category.color}`}>
                  <h3 className="text-lg font-semibold mb-3 flex items-center" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                    {category.icon}
                    <span className="ml-2">{category.title}</span>
                  </h3>
                  <ul className="list-disc pl-5 space-y-2">
                    {teamAnalysis[category.key].map((item: string, idx: number) => (
                      <li key={idx} className="text-base text-gray-700" style={{ fontFamily: '"Open Sans", sans-serif' }}>{item}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    );
  };

  // Render all other fields as generic cards
  const renderOtherFields = (analysis: any) => {
    if (!analysis) return null;
    
    // Skip fields that are handled separately or should be hidden
    const skipFields = [
      'executive_summary', 
      'individual_analysis', 
      'team_analysis', 
      'confidence_score',
      'risk_justification',  // Skip these as standalone fields
      'risk_rating',         // Skip these as standalone fields
      'recommendations'      // Skip recommendations as it's handled separately
    ];
    
    // Check if we need to create a combined risk analysis section
    const hasRiskFields = analysis.risk_justification || analysis.risk_rating;
    const hasRiskAnalysis = analysis.risk_analysis;
    
    // Create a combined risk analysis object if needed
    let combinedRiskAnalysis = null;
    if (hasRiskFields && !hasRiskAnalysis) {
      combinedRiskAnalysis = {
        key: 'risk_analysis',
        value: {
          ...(analysis.risk_justification && { justification: analysis.risk_justification }),
          ...(analysis.risk_rating && { rating: analysis.risk_rating })
        }
      };
    } else if (hasRiskFields && hasRiskAnalysis && typeof analysis.risk_analysis === 'object') {
      // Merge with existing risk analysis
      combinedRiskAnalysis = {
        key: 'risk_analysis',
        value: {
          ...analysis.risk_analysis,
          ...(analysis.risk_justification && { justification: analysis.risk_justification }),
          ...(analysis.risk_rating && { rating: analysis.risk_rating })
        }
      };
      // Skip the original risk_analysis in the main loop
      skipFields.push('risk_analysis');
    }
    
    // Process all regular fields
    const regularFields = Object.entries(analysis)
      .filter(([key]) => !skipFields.includes(key))
      .filter(([_, value]) => {
        // Skip empty values
        if (isEmptyValue(value)) return false;
        
        return true;
      })
      .map(([key, value]) => {
        // Format the key for display using the helper function
        const formattedKey = formatKeyForDisplay(key);
        
        // Special handling for common structured fields
        const commonFields = [
          'execution_capability', 'market_team_fit', 
          'network_assessment', 'risk_analysis', 'team_dynamics'
        ];
        
        // Extra check for market_team_fit and network_assessment to ensure they have meaningful content
        if ((key === 'market_team_fit' || key === 'network_assessment') && typeof value === 'object' && value !== null) {
          // Check if all values are empty or meaningless
          const hasNonEmptyValue = Object.entries(value).some(([_, subValue]) => {
            if (subValue === null || subValue === undefined) return false;
            if (typeof subValue === 'string' && subValue.trim() === '') return false;
            if (Array.isArray(subValue) && (subValue.length === 0 || subValue.every(item => !item || (typeof item === 'string' && item.trim() === '')))) return false;
            
            // For nested objects, check if they have any non-empty values
            if (typeof subValue === 'object' && !Array.isArray(subValue)) {
              return Object.values(subValue).some(v => {
                if (v === null || v === undefined) return false;
                if (typeof v === 'string' && v.trim() === '') return false;
                if (Array.isArray(v) && v.length === 0) return false;
                return true;
              });
            }
            
            return true;
          });
          
          // Skip this section entirely if all values are empty
          if (!hasNonEmptyValue) return null;
        }
        
        if (commonFields.includes(key) && typeof value === 'object') {
          // For fields that contain arrays of strings
          if (Array.isArray(value)) {
            return renderGenericCard(formattedKey, value);
          }
          
          // Filter out empty values from the object
          const filteredEntries = Object.entries(value || {}).filter(([_, subValue]) => {
            if (subValue === null || subValue === undefined) return false;
            if (typeof subValue === 'string' && subValue.trim() === '') return false;
            if (Array.isArray(subValue) && subValue.length === 0) return false;
            if (typeof subValue === 'object' && Object.keys(subValue).length === 0) return false;
            
            // Additional check for objects with all empty values
            if (typeof subValue === 'object' && !Array.isArray(subValue)) {
              const hasNonEmptyValue = Object.values(subValue).some(v => {
                if (v === null || v === undefined) return false;
                if (typeof v === 'string' && v.trim() === '') return false;
                if (Array.isArray(v) && v.length === 0) return false;
                // For deeply nested objects
                if (typeof v === 'object' && v !== null) {
                  return Object.values(v).some(nestedVal => 
                    nestedVal !== null && 
                    nestedVal !== undefined && 
                    !(typeof nestedVal === 'string' && nestedVal.trim() === '')
                  );
                }
                return true;
              });
              return hasNonEmptyValue;
            }
            
            return true;
          });
          
          // If all subvalues are empty, skip this field entirely
          if (filteredEntries.length === 0) return null;
          
          // Additional check for market_team_fit and network_assessment
          if ((key === 'market_team_fit' || key === 'network_assessment') && filteredEntries.length > 0) {
            // Check if any entry has actual content
            const hasActualContent = filteredEntries.some(([_, entryValue]) => {
              if (typeof entryValue === 'string') return entryValue.trim() !== '';
              if (Array.isArray(entryValue)) {
                return entryValue.some(item => item && (typeof item !== 'string' || item.trim() !== ''));
              }
              if (typeof entryValue === 'object' && entryValue !== null) {
                return Object.values(entryValue).some(v => v !== null && v !== undefined && !(typeof v === 'string' && v.trim() === ''));
              }
              return false;
            });
            
            if (!hasActualContent) return null;
          }
          
          // For fields that are objects with multiple properties
          return renderStructuredCard(key, formattedKey, filteredEntries);
        }
        
        // Default rendering for other fields
        return renderGenericCard(formattedKey, value);
      })
      .filter(Boolean); // Remove any null elements that were filtered out
    
    // Add the combined risk analysis if we created one
    if (combinedRiskAnalysis && Object.keys(combinedRiskAnalysis.value).length > 0) {
      const formattedKey = 'Risk Analysis';
      const filteredEntries = Object.entries(combinedRiskAnalysis?.value || {}).filter(([_, subValue]) => {
        if (subValue === null || subValue === undefined) return false;
        if (typeof subValue === 'string' && subValue.trim() === '') return false;
        if (Array.isArray(subValue) && subValue.length === 0) return false;
        if (typeof subValue === 'object' && Object.keys(subValue).length === 0) return false;
        return true;
      });
      
      if (filteredEntries.length > 0) {
        regularFields.push(renderStructuredCard('risk_analysis', formattedKey, filteredEntries));
      }
    }
    
    return regularFields;
  };



  return (
    <div className="min-h-screen bg-background relative">
      <div className="fixed inset-0 -z-10">
        <StarsBackground />
      </div>
      
      {error && (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      )}
      

      
      <div className="relative z-10">
        {/* DashboardHeader is already included in the ProtectedRoute wrapper */}
        <div className="container mx-auto px-4">
          <AnalysisNav id={analysisId || ''} />
          
          {/* LinkedIn URL Input Section - Only show if no team analysis exists */}
          {!teamAnalysis && (
            <div className="w-full pt-8">
              <Card className="p-6 relative bg-transparent border border-gray-200 shadow-2xl backdrop-blur-sm rounded-xl mb-6">
                <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                  <StarsBackground />
                </div>
                <div className="relative z-10">
                  <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center text-left border-b border-gray-300 pb-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                    <PeopleIcon className="mr-2" sx={{ color: '#3b82f6' }} />
                    Add Team LinkedIn Profiles
                  </h2>
                  
                  <div className="space-y-4">
                    {linkedinUrls.map((url, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          type="text"
                          placeholder="Enter LinkedIn profile URL"
                          value={url}
                          onChange={(e) => handleUrlChange(index, e.target.value)}
                          className="flex-1"
                        />
                        {index === linkedinUrls.length - 1 ? (
                          <UiButton 
                            variant="outline" 
                            onClick={addUrlInput}
                            className="min-w-[40px] h-10"
                          >
                            <Plus size={16} />
                          </UiButton>
                        ) : (
                          <UiButton 
                            variant="outline" 
                            onClick={() => removeUrlInput(index)}
                            className="min-w-[40px] h-10"
                          >
                            <Trash size={16} />
                          </UiButton>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
              
              {/* Submit button section */}
              {loading || creditsLoading ? (
                <div className="flex justify-center mt-2">
                  <CircularProgress size={24} />
                </div>
              ) : credits < 5 ? (
                <div className="flex flex-col items-center mt-4">
                  <p className="text-gray-800 text-center mb-4 text-lg">
                    You need 5 credits to analyze the founding team.
                  </p>
                  <div className="text-center mb-4">
                    <a href="/credits" className="text-blue-500 hover:text-blue-700">
                      Get more credits
                    </a>
                  </div>
                  <RainbowButton
                    disabled={true}
                    className="opacity-50 cursor-not-allowed bg-gray-100"
                  >
                    <div className="flex items-center justify-center">
                      <span className="text-red-500">Need More Credits</span>
                    </div>
                  </RainbowButton>
                </div>
              ) : (
                <RainbowButton
                  onClick={handleLinkedinSubmit}
                  disabled={loading || credits < 5}
                  className="mt-2"
                >
                  Analyse founding team
                </RainbowButton>
              )}
            </div>
          )}

          {/* Loading/Progress Message */}
          {profiles.length > 0 && !teamAnalysis && analysisStatus !== 'idle' && (
            <div className="mt-6">
              <TeamAnalysisProgressCard 
                status={analysisStatus === 'idle' ? 'pending' : analysisStatus as any}
                createdAt={analysisStartTime || undefined}
                profileCount={profiles.length}
                onComplete={() => {
                  // Refresh the page to get the completed analysis
                  setTimeout(() => {
                    window.location.reload();
                  }, 1000);
                }}
              />
            </div>
          )}

          {/* Analysis Results */}
          {teamAnalysis && (
            <div className="space-y-6 mt-6">
              {/* Executive Summary */}
              {teamAnalysis.structured_analysis?.executive_summary && 
                renderGenericCard('Executive Summary', teamAnalysis.structured_analysis.executive_summary)}
              
              {/* Individual Analysis */}
              {teamAnalysis.structured_analysis?.individual_analysis && (
                renderIndividualAnalysis(teamAnalysis.structured_analysis.individual_analysis)
              )}
              
              {/* Team SWOT Analysis */}
              {teamAnalysis.structured_analysis?.team_analysis && 
                renderSwotAnalysis(teamAnalysis.structured_analysis.team_analysis)}
              
              {/* Recommendations */}
              {teamAnalysis.structured_analysis?.recommendations && 
                (Array.isArray(teamAnalysis.structured_analysis.recommendations) && 
                 teamAnalysis.structured_analysis.recommendations.length === 0 ? null : (
                  <Card className="p-6 relative bg-transparent border border-gray-200 shadow-2xl backdrop-blur-sm rounded-xl mb-6">
                    <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                      <StarsBackground />
                    </div>
                    <div className="relative z-10">
                      <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center text-left border-b border-gray-300 pb-2">
                        <RecommendIcon className="mr-2" sx={{ color: '#FF9800' }} />
                        Recommendations
                      </h2>
                      
                      <div className="space-y-4 text-left">
                        {Array.isArray(teamAnalysis.structured_analysis.recommendations) ? (
                          <ul className="list-disc pl-5 space-y-1">
                            {teamAnalysis.structured_analysis.recommendations.map((item, idx) => (
                              <li key={idx} className="text-gray-700">
                                {item}
                              </li>
                            ))}
                          </ul>
                        ) : typeof teamAnalysis.structured_analysis.recommendations === 'string' ? (
                          <p className="text-gray-700">
                            {teamAnalysis.structured_analysis.recommendations}
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {typeof teamAnalysis.structured_analysis.recommendations === 'object' ? 
                              Object.entries(teamAnalysis.structured_analysis.recommendations).map(([key, value], idx) => (
                                <div key={idx} className="mb-3">
                                  <p className="text-gray-700 font-medium" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                    {formatKeyForDisplay(key)}:
                                  </p>
                                  <p className="text-gray-700 py-1" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                    {value as string}
                                  </p>
                                </div>
                              ))
                            : 
                              <p className="text-gray-700" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                {String(teamAnalysis.structured_analysis.recommendations)}
                              </p>
                            }
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))
              }
              
              {/* Other structured analysis fields */}
              {teamAnalysis.structured_analysis && 
                renderOtherFields(teamAnalysis.structured_analysis)}
              
              {/* Raw Analysis (fallback) */}
              {(!teamAnalysis.structured_analysis && teamAnalysis?.gpt_analysis?.raw_analysis) && (
                <Card className="relative bg-transparent border-none shadow-2xl backdrop-blur-sm">
                  <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                    <StarsBackground />
                  </div>
                  <Box sx={{ p: 3 }} className="relative z-10">  
                    <Typography variant="h5" gutterBottom>
                      Team Analysis
                    </Typography>
                    
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <div className="prose max-w-none text-gray-700">
                        <ReactMarkdown>
                          {teamAnalysis.gpt_analysis.raw_analysis}
                        </ReactMarkdown>
                      </div>
                    </Paper>
                  </Box>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Alert Dialog */}
      <AlertDialog open={showAlert} onOpenChange={setShowAlert}>
        <AlertDialogContent className="max-w-[360px]">
          <AlertDialogHeader>
            {alertType === 'error' ? (
              <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-red-50">
                <AlertTriangle className="size-5 text-red-600" />
              </div>
            ) : (
              <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-green-50">
                <svg xmlns="http://www.w3.org/2000/svg" className="size-5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
            )}
            <AlertDialogTitle className="text-center">
              {alertType === 'error' ? 'Error' : 'Success'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              {alertMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className={`w-full ${alertType === 'error' ? 'bg-[#2563eb] hover:bg-[#2563eb]/90' : 'bg-green-600 hover:bg-green-600/90'}`}>
              Okay
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Team;